import { Form, Formik } from 'formik'
import React, { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, BoxArrowUpRight, CheckCircleFill, Eye, EyeSlash, Instagram, Linkedin, Tiktok, Youtube } from 'react-bootstrap-icons'
import axiosService from '../../../utils/AxiosService'
import Input from '../../FormComponents/Input'
import { toastError, toastSuccess } from '../../FormComponents/Toasts'

const SOCIAL_FIELDS = [
  {
    name: 'instagram_username',
    label: 'Instagram Username',
    icon: Instagram,
    extractedKey: 'extracted_instagram_username',
    profileUrl: username => `https://www.instagram.com/${username}`,
  },
  {
    name: 'tiktok_username',
    label: 'TikTok Username',
    icon: Tiktok,
    extractedKey: 'extracted_tiktok_username',
    profileUrl: username => `https://www.tiktok.com/@${username}`,
  },
  {
    name: 'youtube_username',
    label: 'YouTube Username',
    icon: Youtube,
    extractedKey: null,
    profileUrl: null,
  },
  {
    name: 'linkedin_username',
    label: 'LinkedIn Username',
    icon: Linkedin,
    extractedKey: null,
    profileUrl: null,
  },
]

export default function PotentialInfluencerPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [potentialInfluencer, setPotentialInfluencer] = useState({})

  const getPotentialInfluencer = () => {
    axiosService.get(`/api/project_manager/potential_influencers/${id}`)
    .then(potentialInfluencer => setPotentialInfluencer(potentialInfluencer))
    .catch(({ data: { error }}) => toastError(error))
  }

  useEffect(() => getPotentialInfluencer(), [])

  // Never deletes the record - only flips `hidden` so registration history (whether/when they
  // signed up) is preserved for data-protection tracking, per client request. This is why it's a
  // toggle on the profile itself rather than a bulk action in the list view.
  const toggleHidden = () => {
    axiosService.patch(`/api/project_manager/potential_influencers/${id}/toggle_hidden`)
    .then(updatedPotentialInfluencer => {
      setPotentialInfluencer(updatedPotentialInfluencer)
      toastSuccess(updatedPotentialInfluencer.hidden ? 'Moved to Hidden list' : 'Moved back to Active list')
    })
    .catch(({ data: { error }}) => toastError(error))
  }

  const createInfluencer = (data) => {
    axiosService.post(`/api/project_manager/potential_influencers/${id}/create_influencer`, { potential_influencer: data })
    .then(({ authentications }) => {
      toastSuccess('Influencer created, details will be created in 5 minutes')
      navigate(`/project_manager/authentications/${authentications[0].id}/influencer/overview`)
    })
    .catch(({ data: { error }}) => toastError(error))
  }

  return (
    <div className="pt-20 pb-16 bg-gray-100 dark:bg-rb-dark-mode min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <Formik
          key={potentialInfluencer.id}
          initialValues={{
            instagram_username: potentialInfluencer.instagram_username || '',
            tiktok_username: potentialInfluencer.tiktok_username || '',
            youtube_username: potentialInfluencer.youtube_username || '',
            linkedin_username: potentialInfluencer.linkedin_username || '',
          }}
          onSubmit={createInfluencer}
        >
          {({ handleBlur, handleChange, values }) => (
            <Form>
              <div className="bg-white dark:bg-rb-dark-mode-lighter rounded-xl shadow-lg overflow-hidden">
                {/* Header */}
                <div className="flex items-start justify-between px-6 py-5 border-b-1 border-rb-lite-grey dark:border-rb-dark-mode">
                  <div className="flex items-center gap-3">
                    <Link
                      to={`/project_manager/potential_influencers${location.state?.listSearch || ''}`}
                      title="Back to Potential Influencers"
                      className="flex items-center justify-center h-9 w-9 rounded-full text-rb-black dark:text-white hover:bg-gray-100 dark:hover:bg-rb-dark-mode transition-colors shrink-0"
                    >
                      <ArrowLeft />
                    </Link>
                    <div>
                      <h1 className="text-xl font-bold text-rb-black dark:text-white">{potentialInfluencer.name}</h1>
                      <p className="text-sm text-gray-500 dark:text-rb-dark-mode-text-grey mt-1">{potentialInfluencer.email}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={toggleHidden}
                    title={potentialInfluencer.hidden
                      ? 'Hidden from the Potential Influencers list. Click to make active again.'
                      : 'Active in the Potential Influencers list. Click to hide.'}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-bold transition-colors shrink-0 ${potentialInfluencer.hidden
                      ? 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-rb-dark-mode dark:text-gray-300 dark:hover:bg-black/30'
                      : 'bg-rb-light-green text-rb-green hover:bg-rb-green hover:text-white dark:bg-rb-green/10 dark:hover:bg-rb-green'}`}
                  >
                    {potentialInfluencer.hidden ? <EyeSlash /> : <Eye />}
                    {potentialInfluencer.hidden ? 'Hidden' : 'Active'}
                  </button>
                </div>

                {/* Social profiles */}
                <div className="px-6 py-6">
                  <p className="text-xs font-bold uppercase tracking-wide text-gray-400 dark:text-rb-dark-mode-text-grey mb-4">
                    Social Profiles
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
                    {SOCIAL_FIELDS.map(({ name, label, icon: Icon, extractedKey, profileUrl }) => {
                      const extractedUsername = extractedKey ? potentialInfluencer[extractedKey] : potentialInfluencer[name]
                      const showExtractedHint = extractedKey && potentialInfluencer[name] && extractedUsername !== potentialInfluencer[name]

                      return (
                        <div key={name} className="mb-4">
                          <Input
                            topMargin=""
                            noLabelPadding
                            label={label}
                            name={name}
                            addon={<span className="flex items-center justify-center w-4"><Icon size={15} /></span>}
                            addonColor="text-gray-400 dark:text-rb-dark-mode-text-grey"
                            inputPaddingClass="pl-2 pr-4"
                            autoComplete="off"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values[name]}
                          />
                          {(showExtractedHint || (profileUrl && extractedUsername)) && (
                            <div className="flex items-center justify-between px-4 mt-1.5 text-xs">
                              {showExtractedHint
                                ? <span className="text-gray-400 dark:text-rb-dark-mode-text-grey">Detected: {extractedUsername}</span>
                                : <span />}
                              {profileUrl && extractedUsername && (
                                <a
                                  target="_blank"
                                  rel="noreferrer"
                                  href={profileUrl(extractedUsername)}
                                  className="flex items-center gap-1 font-bold text-rb-green hover:underline"
                                >
                                  View profile <BoxArrowUpRight />
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Footer / primary action */}
                <div className="px-6 py-4 bg-gray-50 dark:bg-rb-dark-mode border-t-1 border-rb-lite-grey dark:border-rb-dark-mode-lighter flex justify-end">
                  {potentialInfluencer.influencer_created
                    ? (
                      <p className="flex items-center gap-2 text-sm font-bold text-rb-green">
                        <CheckCircleFill /> Influencer already created
                      </p>
                    )
                    : (
                      <button
                        type="submit"
                        className="bg-rb-green hover:bg-rb-green/90 text-white font-bold rounded-lg px-5 py-2.5 transition-colors"
                      >
                        Create Influencer
                      </button>
                    )}
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  )
}
